import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserPlus, Trash, Key, Copy, CheckCircle, Sparkle, X, Scales, Wrench, Briefcase, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import OpeningHoursDesigner from "./components/OpeningHoursDesigner";

export default function Team() {
  const { refreshMode, isSolo } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [openInvite, setOpenInvite] = useState(false);
  const [inviteNote, setInviteNote] = useState("");
  const [inviteRole, setInviteRole] = useState("empleado");
  const [creatingInvite, setCreatingInvite] = useState(false);
  // Sprint H Batch 8 — horario por colaborador
  const [scheduleUser, setScheduleUser] = useState(null);
  const [schedulePerDay, setSchedulePerDay] = useState({});
  const [savingSchedule, setSavingSchedule] = useState(false);

  const openSchedule = (u) => {
    setScheduleUser(u);
    setSchedulePerDay(u.schedule_per_day || {});
  };
  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put(`/users/${scheduleUser.id}`, { schedule_per_day: schedulePerDay });
      toast.success("Horario actualizado");
      setScheduleUser(null);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSavingSchedule(false);
    }
  };

  const load = async () => {
    const [u, i] = await Promise.all([api.get("/users"), api.get("/invites")]);
    setUsers(u.data); setInvites(i.data);
  };
  useEffect(() => { load(); }, []);

  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      const { data } = await api.post("/invites", { note: inviteNote, role: inviteRole });
      const roleLbl = inviteRole === "gestor" ? "Gestor" : inviteRole === "empleado" ? "Empleado" : "Colaborador";
      toast.success(`Código generado (${roleLbl}): ${data.code}`);
      setInviteNote("");
      setInviteRole("empleado");
      setOpenInvite(false);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setCreatingInvite(false); }
  };

  const copyInvite = (code) => {
    const link = `${window.location.origin}/register?invite=${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Enlace de invitación copiado al portapapeles");
  };

  const revokeInvite = async (code) => {
    if (!window.confirm(`¿Revocar el código ${code}?`)) return;
    try {
      await api.delete(`/invites/${code}`);
      toast.success("Código revocado"); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const updateRate = async (u, rate) => {
    try {
      await api.put(`/users/${u.id}`, { retention_irpf_rate: rate });
      toast.success(`Retención IRPF actualizada a ${rate}%`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}? El acceso quedará revocado inmediatamente.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Colaborador eliminado");
      load();
      refreshMode();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const activeInvites = invites.filter((i) => !i.used_at);
  const usedInvites = invites.filter((i) => i.used_at);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Roles y accesos</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1 text-[var(--ag-text)]">Equipo del centro</h1>
          {isSolo && (
            <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 bg-[var(--ag-aqua)]/15 border border-[var(--ag-aqua)]/30 rounded text-[10px] uppercase tracking-wider text-[var(--ag-aqua-soft)]" data-testid="solo-mode-badge">
              <Sparkle size={10} weight="fill" /> Modo Solo Titular — sin reparto de comisión hasta que añadas un colaborador
            </div>
          )}
        </div>
        <Button onClick={()=>setOpenInvite(true)} className="ag-btn-gold rounded-sm" data-testid="new-invite-btn">
          <Key className="mr-1.5" weight="fill" /> Generar invitación
        </Button>
      </header>

      {/* Active invitation codes */}
      <section className="ag-card p-5" data-testid="invites-section">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Códigos de invitación</div>
            <div className="text-sm text-[var(--ag-text)]">
              {activeInvites.length} activo{activeInvites.length === 1 ? "" : "s"} · {usedInvites.length} utilizado{usedInvites.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        {activeInvites.length === 0 && usedInvites.length === 0 && (
          <div className="text-sm text-[var(--ag-text-dim)] py-4 text-center">
            Aún no has generado ningún código. Genera uno para invitar a un colaborador a tu centro.
          </div>
        )}
        <div className="space-y-2">
          {activeInvites.map((inv) => (
            <div key={inv.code} className="flex items-center gap-3 p-3 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded hover:border-[var(--ag-gold)]/40 transition-colors" data-testid={`invite-active-${inv.code}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono-tab text-base tracking-[0.25em] font-bold text-[var(--ag-gold)]">{inv.code}</div>
                  {inv.role === "gestor" ? (
                    <span className="text-[9px] uppercase tracking-wider bg-[var(--ag-gold)]/15 border border-[var(--ag-gold)]/40 text-[var(--ag-gold)] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                      <Scales size={9} weight="fill" /> Gestor
                    </span>
                  ) : inv.role === "empleado" ? (
                    <span className="text-[9px] uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                      <Wrench size={9} weight="fill" /> Empleado
                    </span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-wider bg-[var(--ag-aqua)]/15 border border-[var(--ag-aqua)]/40 text-[var(--ag-aqua-soft)] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                      <Briefcase size={9} weight="fill" /> Colaborador
                    </span>
                  )}
                </div>
                {inv.note && <div className="text-xs text-[var(--ag-text-dim)] mt-0.5 truncate">{inv.note}</div>}
                <div className="text-[10px] text-[var(--ag-text-dim)] mt-1">Generado {new Date(inv.created_at).toLocaleDateString("es-ES")}</div>
              </div>
              <Button size="sm" variant="outline" onClick={()=>copyInvite(inv.code)} className="rounded-sm border-[var(--ag-border)] text-[var(--ag-text)] hover:bg-[var(--ag-midnight-soft)]" data-testid={`copy-invite-${inv.code}`}>
                <Copy size={13} className="mr-1" /> Copiar enlace
              </Button>
              <Button size="icon" variant="ghost" onClick={()=>revokeInvite(inv.code)} className="hover:bg-red-500/10" data-testid={`revoke-invite-${inv.code}`}>
                <X size={15} className="text-red-400" />
              </Button>
            </div>
          ))}
          {usedInvites.map((inv) => (
            <div key={inv.code} className="flex items-center gap-3 p-3 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded opacity-70" data-testid={`invite-used-${inv.code}`}>
              <CheckCircle size={18} weight="fill" className="text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono-tab text-sm tracking-[0.2em] text-[var(--ag-text-dim)] line-through">{inv.code}</div>
                <div className="text-[10px] text-[var(--ag-text-dim)]">Utilizado por <b className="text-[var(--ag-text)]/80">{inv.used_by_email}</b> · {new Date(inv.used_at).toLocaleDateString("es-ES")}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Team list */}
      <section>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] mb-2">Miembros del centro</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {users.map((u) => (
            <div key={u.id} className="metric-card p-5" data-testid={`team-card-${u.id}`}>
              <div className="flex items-start gap-3">
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt={u.name} className="w-12 h-12 rounded-full object-cover border border-[var(--ag-border)]" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[var(--ag-midnight)] border border-[var(--ag-border)] grid place-items-center font-display font-bold text-[var(--ag-gold)] text-base shrink-0">
                    {u.name?.charAt(0).toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-semibold text-[var(--ag-text)] truncate">{u.name}</div>
                  <div className="text-sm text-[var(--ag-text-dim)] truncate">{u.email}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-aqua-soft)]">{u.role}</div>
                </div>
                {u.role !== "admin" && (
                  <Button size="icon" variant="ghost" onClick={()=>del(u)} data-testid={`del-user-${u.id}`}><Trash size={14} className="text-red-400" /></Button>
                )}
              </div>
              {u.role === "colaborador" && (
                <>
                  <div className="mt-4 flex items-center gap-2">
                    <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Retención IRPF</Label>
                    <Input type="number" step="0.1" defaultValue={u.retention_irpf_rate}
                      onBlur={(e)=>{ const v = Number(e.target.value); if (v !== u.retention_irpf_rate) updateRate(u, v); }}
                      className="w-20 h-8 rounded-sm tabular-nums text-right" />
                    <span className="text-xs text-[var(--ag-text-dim)]">%</span>
                  </div>
                  <div className="mt-3">
                    <Button
                      onClick={() => openSchedule(u)}
                      variant="outline" size="sm"
                      className="w-full rounded-sm border-[var(--ag-border)] text-xs gap-2 justify-start"
                      data-testid={`team-schedule-${u.id}`}
                    >
                      <Clock size={13} weight="duotone" className="text-[var(--ag-aqua-soft)]" />
                      Horario comercial del colaborador
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      <Dialog open={openInvite} onOpenChange={setOpenInvite}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tighter">Generar invitación</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--ag-text-dim)]">
              Generaremos un código único de 8 caracteres. Compártelo o usa el <b className="text-[var(--ag-text)]">enlace directo</b> (al hacer clic, la persona va al registro pre-rellenado). Cada invitación es de un solo uso y vincula <b className="text-[var(--ag-text)]">exclusivamente</b> a tu centro.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider">Rol al unirse *</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="empleado" data-testid="invite-role-empleado">
                    <span className="flex items-center gap-2"><Wrench size={13} weight="duotone" className="text-emerald-400" /> Empleado / Operario · Ve agenda, marca «vehículo listo»</span>
                  </SelectItem>
                  <SelectItem value="colaborador" data-testid="invite-role-colaborador">
                    <span className="flex items-center gap-2"><Briefcase size={13} weight="duotone" className="text-[var(--ag-aqua-soft)]" /> Colaborador · Autónomo · Reparto de comisión</span>
                  </SelectItem>
                  <SelectItem value="gestor" data-testid="invite-role-gestor">
                    <span className="flex items-center gap-2"><Scales size={13} weight="duotone" className="text-[var(--ag-gold)]" /> Gestor · Contable / Asesor (solo Gastos & AEAT)</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[var(--ag-text-dim)] mt-1">
                {inviteRole === "gestor"
                  ? "Acceso limitado a Gastos y Modelos AEAT. No verá Servicios ni cobrará comisiones. Requiere plan Ecosystem Full."
                  : inviteRole === "empleado"
                  ? "Acceso a la agenda del centro y a marcar 'coche listo'. Sin acceso a finanzas, caja ni impuestos. Disponible en cualquier plan, sin coste extra."
                  : "Acceso a Servicios, Agenda, Clientes, Gastos y su propia Disponibilidad. Reparto de comisión configurable. Requiere plan Ecosystem Full."}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nota interna (opcional)</Label>
              <Input value={inviteNote} onChange={(e)=>setInviteNote(e.target.value)} className="mt-1 rounded-sm"
                placeholder="Ej. para Pedro (jornada parcial)" data-testid="invite-note" />
              <p className="text-[11px] text-[var(--ag-text-dim)] mt-1">Solo visible para ti, te ayudará a identificar para quién es.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenInvite(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={createInvite} disabled={creatingInvite} className="ag-btn-gold rounded-sm" data-testid="invite-submit">
              {creatingInvite ? "Generando…" : <><Key size={14} weight="fill" className="mr-1.5" /> Generar código</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint H Batch 8 — Dialog: editar horario del colaborador */}
      <Dialog open={!!scheduleUser} onOpenChange={(v)=>!v && setScheduleUser(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tighter" data-testid="schedule-dialog-title">
              Horario de {scheduleUser?.name || "colaborador"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--ag-text-dim)]">
              Define los días y franjas en los que este colaborador atiende citas.
              Si no se define horario, se hereda el horario comercial del centro.
            </p>
            <OpeningHoursDesigner
              value={schedulePerDay}
              onChange={setSchedulePerDay}
              testIdPrefix="colab-schedule"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setScheduleUser(null)} className="rounded-sm">Cancelar</Button>
            <Button onClick={saveSchedule} disabled={savingSchedule} className="ag-btn-gold rounded-sm" data-testid="schedule-save">
              <Clock size={14} weight="fill" className="mr-1.5" />
              {savingSchedule ? "Guardando…" : "Guardar horario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Restored legacy "Add colaborador directly" entry-point removed in favour of invite codes.
// Customers who prefer the old direct-create flow can still hit POST /api/users from API.
export function _legacy_marker() { return null; }
